import React, { useState, useEffect } from 'react';
import { Modal, TextArea, M, Button } from 'design-system-web';
import { useTranslation } from 'react-i18next';
import styles from './CancelRequestModal.scss';
import { Alert, AlertType } from '../Alert/Alert';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (note: string) => Promise<boolean>;
  error?: string | null;
  isLoading?: boolean;
};

export function CancelRequestModal({ isOpen, onClose, onSubmit, error, isLoading }: Props) {
  const { t } = useTranslation();
  const [note, setNote] = useState<string>('');
  const maxLength = 400;

  useEffect(() => {
    if (!isOpen) {
      setNote('');
    }
  }, [isOpen]);

  const handleNoteChange = (value: string) => {
    if (value.length <= maxLength) {
      setNote(value);
    }
  };

  const handleDelete = async () => {
    if (!note.trim() || isLoading) return;

    const success = await onSubmit(note.trim());
    
    if (success) {
      setNote('');
      onClose();
    }
  };

  const handleClose = () => {
    setNote('');
    onClose();
  };

  const isDeleteDisabled = !note.trim() || isLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('shared.integrations.vantify.cancelmodal.title')}
      size="large"
      className={styles.modal}
      actions={
        <div className={styles.actionsContainer}>
          <Button
            variant="primary"
            className={styles.deleteButton}
            onClick={handleDelete}
            disabled={isDeleteDisabled}
            loading={isLoading}
          >
            {isLoading
              ? t('shared.integrations.vantify.workorder.submitLoading')
              : t('shared.integrations.vantify.cancelmodal.submit')}
          </Button>
          <Button
            variant="secondary"
            className={styles.returnButton}
            onClick={handleClose}
          >
            {t('shared.integrations.vantify.cancelmodal.returntopage')}
          </Button>
        </div>
      }
    >
      <div className={styles.content}>
        {error && (
          <Alert type={AlertType.error} fullWidth>
            {error}
          </Alert>
        )}
        <M className={styles.confirmationMessage}>
          {t('shared.integrations.vantify.cancelmodal.confirmation')}
        </M>
        <div className={styles.noteContainer}>
          <label className={styles.noteLabel}>
            {t('shared.integrations.vantify.cancelmodal.note')} <span className={styles.requiredAsterisk}>{t('shared.integrations.vantify.cancelmodal.aestrick')}</span>
          </label>
          <TextArea
            placeholder={t('shared.integrations.vantify.cancelmodal.note.placeholder')}
            value={note}
            onChange={handleNoteChange}
            maxLength={maxLength}
            showLengthIndicator
            className={styles.noteTextArea}
            disabled={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
}